import { configureStore, type Middleware, type Reducer } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import type { Api, BaseQueryFn, EndpointDefinitions } from '@reduxjs/toolkit/query';
import { lociApi } from '@loci/api';
import { performanceLoggerMiddleware } from './middleware/performanceLogger';
import { rtkQueryErrorMiddleware } from './middleware/notificationMiddleware';
import notificationReducer from './slices/notificationSlice';
import mapReducer from './slices/mapSlice';
import preferenceReducer from './slices/preferenceSlice';
import recentEventsReducer, {
  hydrateRecentEventsFromStorage,
  persistRecentEventsToStorage,
  selectRecentEvents,
  type RecentEvent,
} from './slices/recentEventsSlice';

/** Must match tagTypes in @loci/api lociApi. Kept in sync manually until LociTagType is exported from dist. */
type LociTagType = 'Event' | 'User' | 'Category' | 'Location' | 'Recommendation' | 'Trending';

// TODO(API-1178): cross-package type mismatch between @loci/api's lociApi and RTK Query's Api<>.
// Remove this double-cast once the tagTypes mismatch is resolved in @loci/api.
const api = lociApi as unknown as Api<BaseQueryFn, EndpointDefinitions, 'lociApi', LociTagType>;

/**
 * Configuration for additional API slices that can be added to the store.
 * Used by apps like web to add app-specific APIs (e.g., authApi).
 */
export interface AdditionalApiConfig {
  reducerPath: string;
  reducer: Reducer;
  middleware: Middleware;
}

/**
 * Creates a configured Redux store with optional additional API slices and
 * preloaded state.
 *
 * In the browser, the `recentEvents` slice is auto-hydrated from
 * `localStorage` so the persisted "Recent" list survives reloads. Callers
 * may pass an explicit `preloadedState` to override (used by tests).
 *
 * @param additionalApis - Additional API slice configs to merge in
 * @param options.preloadedState - Optional override for slice initial state
 */
/** Optional override for slice initial state (typed loosely to avoid forcing
 *  callers to enumerate every reducer key). */
type AppStorePreloadedState = Record<string, unknown>;

export function createAppStore(
  additionalApis: AdditionalApiConfig[] = [],
  options: { preloadedState?: AppStorePreloadedState } = {},
) {
  const additionalReducers = additionalApis.reduce(
    (acc, apiConfig) => {
      acc[apiConfig.reducerPath] = apiConfig.reducer;
      return acc;
    },
    {} as Record<string, Reducer>,
  );

  const additionalMiddlewares = additionalApis.map((apiConfig) => apiConfig.middleware);

  // Hydrate `recentEvents` from localStorage in the browser so the persisted
  // history survives reloads. SSR / Node runs (no `window`) get an empty
  // slice. Explicit `options.preloadedState` always wins.
  const preloadedState: AppStorePreloadedState | undefined =
    options.preloadedState ??
    (typeof window !== 'undefined'
      ? { recentEvents: hydrateRecentEventsFromStorage() }
      : undefined);

  const store = configureStore({
    reducer: {
      [api.reducerPath]: api.reducer,
      notification: notificationReducer,
      map: mapReducer,
      preference: preferenceReducer,
      recentEvents: recentEventsReducer,
      ...additionalReducers,
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({ serializableCheck: false })
        .concat(performanceLoggerMiddleware)
        .concat(api.middleware)
        .concat(rtkQueryErrorMiddleware)
        .concat(...additionalMiddlewares),
    devTools: process.env.NODE_ENV !== 'production',
    // Cast widens the loose `Record<string, unknown>` shape to whatever the
    // configureStore generic narrows to; the reducer always provides defaults
    // for unspecified keys, so partial preload is always valid.
    preloadedState: preloadedState as Parameters<typeof configureStore>[0]['preloadedState'],
  });

  setupListeners(store.dispatch);

  // Persist recentEvents on every state change. Reducers stay pure; the
  // localStorage write happens here, after each commit. Skip on the server.
  if (typeof window !== 'undefined') {
    type StateLike = { recentEvents: { events: RecentEvent[] } };
    const getEvents = () => selectRecentEvents(store.getState() as unknown as StateLike as never);
    let lastEvents = getEvents();
    store.subscribe(() => {
      const nextEvents = getEvents();
      if (nextEvents !== lastEvents) {
        lastEvents = nextEvents;
        persistRecentEventsToStorage(nextEvents);
      }
    });
  }

  return store;
}

export const store = createAppStore();

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
